import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  GraduationCap, 
  CreditCard, 
  CheckCircle, 
  Mail,
  Shield,
  Loader2,
  BookOpen,
  ArrowRight,
  ArrowLeft,
  Lock,
  User,
  Clock
} from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../components/ui/input-otp";

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, login } = useAuth();
  
  // Steps: 1 = Details, 2 = Course Selection, 3 = Payment, 4 = OTP Verification, 5 = Complete
  const [currentStep, setCurrentStep] = useState(1);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    course_id: ""
  });
  
  const [otp, setOtp] = useState("");
  const [paymentSessionId, setPaymentSessionId] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    fetchCourses();
    
    // Check for pre-selected course from URL
    const courseId = searchParams.get('course');
    if (courseId) {
      setFormData(prev => ({ ...prev, course_id: courseId }));
      setCurrentStep(1); // Start from step 1 but with course pre-selected
    }
    
    // Check if returning from payment
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setPaymentSessionId(sessionId);
      checkPaymentStatus(sessionId);
    }
    
    // Check if payment cancelled
    if (searchParams.get('cancelled') === 'true') {
      toast.error("Payment was cancelled. Please try again.");
      setCurrentStep(3);
    }
  }, [searchParams]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data.filter(c => c.price > 0)); // Only show paid courses
    } catch (error) {
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        if (response.data.requires_email_verification) {
          setCurrentStep(4); // Go to OTP verification
          toast.success("Payment successful! Please verify your email.");
        } else if (response.data.user_created) {
          setCurrentStep(5); // Go to complete
          toast.success("Registration complete!");
        }
      }
    } catch (error) {
      toast.error("Failed to verify payment status");
    }
  };

  const validateStep1 = () => {
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return false;
    }
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!formData.password || formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.course_id) {
      toast.error("Please select a course");
      return false;
    }
    return true;
  };

  const handleStep1Submit = async () => {
    if (!validateStep1()) return;
    setCurrentStep(2);
  };

  const handleStep2Submit = async () => {
    if (!validateStep2()) return;
    
    setProcessing(true);
    try {
      // Initiate registration (sends OTP)
      await axios.post(`${API}/auth/register/initiate`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: "student",
        course_id: formData.course_id
      });
      
      toast.success("Verification code sent to your email");
      setCurrentStep(3); // Go to payment
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const selectedCourse = courses.find(c => c.course_id === formData.course_id);
      
      const response = await axios.post(`${API}/payments/create-checkout`, {
        course_id: formData.course_id,
        email: formData.email,
        name: formData.name,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment session");
      setProcessing(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(`${API}/auth/register/verify-otp`, {
        email: formData.email,
        otp: otp
      });
      
      // Complete registration
      const response = await axios.post(`${API}/auth/register/complete?email=${encodeURIComponent(formData.email)}`);
      
      toast.success("Registration complete! Welcome to the platform.");
      
      // Auto-login
      if (response.data.session_token) {
        login(response.data);
        navigate('/dashboard');
      } else {
        setCurrentStep(5);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    try {
      await axios.post(`${API}/auth/register/resend-otp?email=${encodeURIComponent(formData.email)}`);
      toast.success("New verification code sent");
      setResendCooldown(60);
    } catch (error) {
      toast.error("Failed to resend code");
    }
  };

  const selectedCourse = courses.find(c => c.course_id === formData.course_id);

  const steps = [
    { number: 1, title: "Your Details", icon: User },
    { number: 2, title: "Select Course", icon: BookOpen },
    { number: 3, title: "Payment", icon: CreditCard },
    { number: 4, title: "Verify Email", icon: Mail },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="h-8 w-8 animate-spin text-violet-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-stone-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <GraduationCap className="h-8 w-8 text-violet-700" />
            <span className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
              LearnHub
            </span>
          </Link>
          <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
            Create Your Account
          </h1>
          <p className="text-stone-600 mt-2">Start your learning journey today</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8 relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-stone-200 -z-10"></div>
          {steps.map((step, index) => (
            <div key={step.number} className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                currentStep >= step.number 
                  ? 'bg-violet-700 text-white' 
                  : 'bg-stone-200 text-stone-500'
              }`}>
                {currentStep > step.number ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <step.icon className="h-5 w-5" />
                )}
              </div>
              <span className={`text-xs mt-2 ${
                currentStep >= step.number ? 'text-violet-700 font-medium' : 'text-stone-500'
              }`}>
                {step.title}
              </span>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="shadow-lg">
          <CardContent className="p-8">
            {/* Step 1: User Details */}
            {currentStep === 1 && (
              <div className="space-y-6" data-testid="step-1">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-stone-900">Enter Your Details</h2>
                  <p className="text-stone-600 text-sm mt-1">We'll use this to create your account</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Minimum 8 characters"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      data-testid="password-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Re-enter your password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={handleStep1Submit}
                  className="w-full bg-violet-700 hover:bg-violet-800"
                  data-testid="step-1-next"
                >
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                
                <p className="text-center text-sm text-stone-600">
                  Already have an account?{" "}
                  <Link to="/login" className="text-violet-700 hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
            )}

            {/* Step 2: Course Selection */}
            {currentStep === 2 && (
              <div className="space-y-6" data-testid="step-2">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-stone-900">Select Your Course</h2>
                  <p className="text-stone-600 text-sm mt-1">Choose the course you want to enroll in</p>
                </div>
                
                <div className="grid gap-4 max-h-96 overflow-y-auto">
                  {courses.map((course) => (
                    <div
                      key={course.course_id}
                      onClick={() => setFormData({ ...formData, course_id: course.course_id })}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.course_id === course.course_id
                          ? 'border-violet-700 bg-violet-50'
                          : 'border-stone-200 hover:border-violet-300'
                      }`}
                      data-testid={`course-${course.course_id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-20 h-16 bg-stone-100 rounded-lg flex-shrink-0 overflow-hidden">
                          {course.thumbnail ? (
                            <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookOpen className="h-6 w-6 text-stone-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-stone-900">{course.title}</h3>
                          <p className="text-sm text-stone-600 line-clamp-1">{course.short_description || course.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className="bg-violet-100 text-violet-700">
                              £{course.price?.toFixed(2)}
                            </Badge>
                            {course.duration && (
                              <span className="text-xs text-stone-500 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {course.duration}
                              </span>
                            )}
                          </div>
                        </div>
                        {formData.course_id === course.course_id && (
                          <CheckCircle className="h-6 w-6 text-violet-700 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {courses.length === 0 && (
                  <div className="text-center py-8 text-stone-500">
                    <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No courses available at the moment</p>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline"
                    onClick={() => setCurrentStep(1)}
                    className="flex-1"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleStep2Submit}
                    disabled={!formData.course_id || processing}
                    className="flex-1 bg-violet-700 hover:bg-violet-800"
                    data-testid="step-2-next"
                  >
                    {processing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Continue to Payment
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Payment */}
            {currentStep === 3 && (
              <div className="space-y-6" data-testid="step-3">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-stone-900">Complete Payment</h2>
                  <p className="text-stone-600 text-sm mt-1">Secure payment via Stripe</p>
                </div>
                
                {selectedCourse && (
                  <div className="p-4 bg-stone-50 rounded-lg">
                    <h3 className="font-medium text-stone-900">{selectedCourse.title}</h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-stone-600">Course Fee</span>
                      <span className="text-xl font-bold text-violet-700">
                        £{selectedCourse.price?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="p-4 bg-violet-50 rounded-lg border border-violet-100">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-violet-700 mt-0.5" />
                    <div>
                      <p className="font-medium text-stone-900">Secure Payment</p>
                      <p className="text-sm text-stone-600">
                        Your payment is processed securely through Stripe. We never store your card details.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-stone-900">3-Month Access</p>
                      <p className="text-sm text-stone-600">
                        Your enrollment will be valid for 3 months from the date of registration.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline"
                    onClick={() => setCurrentStep(2)}
                    className="flex-1"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={handlePayment}
                    disabled={processing}
                    className="flex-1 bg-violet-700 hover:bg-violet-800"
                    data-testid="pay-button"
                  >
                    {processing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Pay £{selectedCourse?.price?.toFixed(2)}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: OTP Verification */}
            {currentStep === 4 && (
              <div className="space-y-6" data-testid="step-4">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-violet-700" />
                  </div>
                  <h2 className="text-xl font-semibold text-stone-900">Verify Your Email</h2>
                  <p className="text-stone-600 text-sm mt-1">
                    We've sent a 6-digit code to<br />
                    <span className="font-medium text-stone-900">{formData.email}</span>
                  </p>
                </div>
                
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={setOtp}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                
                <Button 
                  onClick={handleVerifyOTP}
                  disabled={otp.length !== 6 || processing}
                  className="w-full bg-violet-700 hover:bg-violet-800"
                  data-testid="verify-otp-button"
                >
                  {processing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Verify & Complete Registration
                    </>
                  )}
                </Button>
                
                <div className="text-center">
                  <p className="text-sm text-stone-600">
                    Didn't receive the code?{" "}
                    <button
                      onClick={handleResendOTP}
                      disabled={resendCooldown > 0}
                      className={`${
                        resendCooldown > 0 
                          ? 'text-stone-400 cursor-not-allowed' 
                          : 'text-violet-700 hover:underline'
                      }`}
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                    </button>
                  </p>
                </div>
              </div>
            )}

            {/* Step 5: Complete */}
            {currentStep === 5 && (
              <div className="text-center space-y-6" data-testid="step-5">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                
                <div>
                  <h2 className="text-2xl font-bold text-stone-900">Registration Complete!</h2>
                  <p className="text-stone-600 mt-2">
                    Welcome to LearnHub! Your account has been created successfully.
                  </p>
                </div>
                
                <Button 
                  onClick={() => navigate('/login')}
                  className="bg-violet-700 hover:bg-violet-800"
                >
                  Sign In to Your Account
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
